import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { img, thumbImg } from "@/lib/images";

const FOLDER = "balivwtour/vw181-kuebelwagen";

interface Photo {
  src: string;
  altKey: string;
}

const photos: Photo[] = [
  { src: `${FOLDER}/vw181car_particip01.jpg`, altKey: "kw_gal_particip" },
  { src: `${FOLDER}/vw181cars02.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars03.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars04.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars05.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars06.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars07.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars08.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars10.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181car01.jpg`, altKey: "kw_gal_car" },
  { src: `${FOLDER}/vw181car_particip02.jpg`, altKey: "kw_gal_particip" },
  { src: `${FOLDER}/vw181cars11.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars12.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars13.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars14.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars15.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars16.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars17.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars18.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars19.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars20.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars21.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars22.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars23.jpg`, altKey: "kw_gal_tour" },
  { src: `${FOLDER}/vw181cars24.jpg`, altKey: "kw_gal_tour" },
];

const PREVIEW_COUNT = 12;

export default function KuebelGallery() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  const close = useCallback(() => setOpenIndex(null), []);

  const step = useCallback((direction: number) => {
    setOpenIndex((current) => {
      if (current === null) return current;
      return (current + direction + photos.length) % photos.length;
    });
  }, []);

  useEffect(() => {
    if (openIndex === null) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [openIndex, close, step]);

  const visiblePhotos = showAll ? photos : photos.slice(0, PREVIEW_COUNT);

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-2xl mb-10">
          <div className="eyebrow-brand text-xs md:text-sm uppercase tracking-[0.35em] mb-3">
            — {t("kw_eyebrow")} —
          </div>
          <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 leading-tight mb-4">
            {t("kw_gallery_title")}
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
            {t("kw_gallery_hint")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {visiblePhotos.map((photo, index) => (
            <button
              key={photo.src}
              type="button"
              onClick={() => setOpenIndex(index)}
              className="group relative rounded-lg overflow-hidden border border-background-200 bg-background-50 aspect-[4/3] cursor-pointer"
              aria-label={t(photo.altKey)}
            >
              <img
                src={thumbImg(photo.src, 800)}
                alt={t(photo.altKey)}
                title={`Bali VW Tour – ${t(photo.altKey)}`}
                className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></span>
              <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-background-50/90 text-foreground-950 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <i className="ri-zoom-in-line"></i>
              </span>
            </button>
          ))}
        </div>

        {photos.length > PREVIEW_COUNT && (
          <div className="mt-10 flex justify-center">
            <button
              type="button"
              onClick={() => setShowAll((current) => !current)}
              className="inline-flex items-center gap-2 border border-foreground-300 text-foreground-800 hover:bg-accent-100 hover:border-accent-400 hover:text-foreground-950 px-7 py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className={showAll ? "ri-arrow-up-s-line" : "ri-gallery-line"}></i>
              {showAll ? t("kw_gal_show_less") : t("kw_gal_show_all", { count: photos.length })}
            </button>
          </div>
        )}
      </div>

      {openIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 md:p-10"
          role="dialog"
          aria-modal="true"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 rounded-full bg-red-600 text-white hover:bg-red-700 flex items-center justify-center cursor-pointer shadow-lg transition-colors"
            aria-label="Schließen"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              step(-1);
            }}
            className="absolute left-3 md:left-8 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/85 text-foreground-950 flex items-center justify-center cursor-pointer"
            aria-label="Vorheriges Bild"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>

          <figure
            className="max-w-5xl w-full flex flex-col items-center gap-4"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={img(photos[openIndex].src)}
              alt={t(photos[openIndex].altKey)}
              className="w-full max-h-[72vh] object-contain rounded-md"
            />
            <figcaption className="text-background-100 text-sm text-center">
              {t(photos[openIndex].altKey)} · {openIndex + 1} / {photos.length}
            </figcaption>
          </figure>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              step(1);
            }}
            className="absolute right-3 md:right-8 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/85 text-foreground-950 flex items-center justify-center cursor-pointer"
            aria-label="Nächstes Bild"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>
      )}
    </section>
  );
}